/**
 * EnvSwitch + DemoBanner — the admin dashboard's dev/prod backend control.
 *
 * EnvSwitch is a master toggle in the header (OFF = Production, ON = Demo). It
 * only appears when the demo backend is compiled into this build (DEMO_AVAILABLE,
 * i.e. VITE_DEMO_SUPABASE_* are set). Flipping it persists the choice and reloads
 * the dashboard against the chosen backend. DemoBanner is a persistent bar shown
 * on every page while the admin is pointed at the demo backend, so staff never
 * mistake demo data for production.
 *
 * When DEV_SWITCH_ENABLED (VITE_DEV_ENV_SWITCH=1), additional Prod/Dev buttons
 * appear that write the control-plane row in dev_runtime_config and subscribe to
 * realtime changes (with a diff guard to avoid reload loops).
 */
import React, { useEffect } from 'react'
import { Switch, Tooltip, Button, Space, Tag, theme, message } from 'antd'
import { brand } from '../theme/brand'
import { DEMO_AVAILABLE, getActiveEnv, isLocalBackend, switchAdminEnv } from '../lib/adminEnv'
import { DEV_SWITCH_ENABLED, readCachedPointerSync } from '../lib/devRuntimeFetch'
import { supabaseClient, ACTIVE_BACKEND_URL } from '../lib/supabaseClient'
import { resolveActiveBackend } from '@muaa/shared'

/** Write the singleton row and immediately reload (realtime also reloads remote tabs). */
async function setActiveEnv(active_env: 'prod' | 'dev') {
  const { error } = await supabaseClient
    .from('dev_runtime_config')
    .update({ active_env, updated_at: new Date().toISOString() })
    .eq('id', true)
  if (error) {
    void message.error('Could not switch environment: ' + error.message)
    return
  }
  // Realtime subscription (below) handles remote tabs; reload self immediately for snappiness.
  window.location.reload()
}

/** Hook: subscribe to dev_runtime_config changes and reload when the resolved target changes.
 *  Reuses the same diff guard as supabaseClient.ts to prevent reload loops. */
function useDevRuntimeReload() {
  useEffect(() => {
    if (!DEV_SWITCH_ENABLED) return
    const baked = getActiveEnv()
    const cached = readCachedPointerSync()
    const current = resolveActiveBackend({
      devModeEnabled: true,
      pointer: null,
      cached,
      prod: { url: baked.url, anonKey: baked.anonKey },
    })

    const channel = supabaseClient
      .channel('dev_runtime_config_changes')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'dev_runtime_config' },
        (payload) => {
          const row = payload.new as { active_env?: string; dev_url?: string; dev_anon_key?: string }
          const next = resolveActiveBackend({
            devModeEnabled: true,
            pointer: { activeEnv: (row.active_env as 'prod' | 'dev') ?? 'prod', devUrl: row.dev_url ?? null, devAnonKey: row.dev_anon_key ?? null },
            cached: null,
            prod: { url: baked.url, anonKey: baked.anonKey },
          })
          // Diff guard: only reload when the effective URL/key changes.
          if (next.url !== current.url || next.anonKey !== current.anonKey) {
            window.location.reload()
          }
        }
      )
      .subscribe()

    return () => {
      void supabaseClient.removeChannel(channel)
    }
  }, [])
}

/**
 * EnvBadge — a single glanceable pill in the header telling you, at runtime, which
 * BACKEND this dashboard build is wired to, so the two look-alike tabs (dev :3001 on
 * a local 127.0.0.1 Supabase vs prod :4173 on the hosted project) can't be confused.
 *
 * Derived purely from the RESOLVED Supabase URL (ACTIVE_BACKEND_URL, which already
 * accounts for the dev-switch pointer): a loopback host → a calm gold/amber
 * "DEV · local" pill; anything hosted → a higher-alert red "LIVE · production" pill.
 * Colors come from the AntD theme tokens (colorWarning / colorError), not hardcoded
 * hex, so it re-skins with the panel. One small Tag — deliberately not a banner.
 */
export function EnvBadge({ url = ACTIVE_BACKEND_URL }: { url?: string }) {
  const { token } = theme.useToken()
  const local = isLocalBackend(url)

  const label = local ? 'DEV · local' : 'LIVE · production'
  // Gold/amber = calm dev; red/danger = higher-alert production.
  const color = local ? token.colorWarning : token.colorError

  return (
    <Tooltip
      title={
        local
          ? 'This dashboard is wired to a LOCAL Supabase backend (development data).'
          : 'This dashboard is wired to the HOSTED production Supabase backend — changes are LIVE.'
      }
    >
      <Tag
        data-testid="muaa-admin-env-badge"
        aria-label={local ? 'Backend: DEV local' : 'Backend: LIVE production'}
        color={color}
        style={{
          margin: 0,
          fontFamily: brand.fSans,
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          borderRadius: 999,
          padding: '2px 10px',
        }}
      >
        {label}
      </Tag>
    </Tooltip>
  )
}

export function EnvSwitch() {
  useDevRuntimeReload()

  const env = getActiveEnv()
  const onDemo = !env.isProd

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {DEV_SWITCH_ENABLED && (
        <Space size={4}>
          <Button
            size="small"
            data-testid="muaa-admin-env-prod"
            onClick={() => void setActiveEnv('prod')}
            style={{ fontFamily: brand.fSans, fontSize: 11, fontWeight: 700 }}
          >
            Prod
          </Button>
          <Button
            size="small"
            data-testid="muaa-admin-env-dev"
            onClick={() => void setActiveEnv('dev')}
            style={{ fontFamily: brand.fSans, fontSize: 11, fontWeight: 700 }}
          >
            Dev
          </Button>
        </Space>
      )}
      {DEMO_AVAILABLE && (
        <>
          <span
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: onDemo ? brand.gold : brand.sub,
            }}
          >
            {onDemo ? 'Demo' : 'Live'}
          </span>
          <Tooltip
            title={
              onDemo
                ? 'Pointed at the demo backend — switch back to production'
                : 'Pointed at production — switch to the demo backend'
            }
          >
            <Switch
              checked={onDemo}
              aria-label="Toggle demo backend"
              onChange={(checked) => switchAdminEnv(checked ? 'demo' : 'prod')}
              style={onDemo ? { background: brand.gold } : undefined}
            />
          </Tooltip>
        </>
      )}
    </div>
  )
}

export function DemoBanner() {
  if (!DEMO_AVAILABLE) return null
  const env = getActiveEnv()
  if (env.isProd) return null

  return (
    <div
      style={{
        background: 'rgba(196,151,49,.12)',
        borderBottom: '1px solid rgba(196,151,49,.32)',
        padding: '8px 28px',
        textAlign: 'center',
        fontFamily: brand.fSans,
        fontWeight: 700,
        fontSize: 12.5,
        letterSpacing: '.02em',
        color: brand.ink,
      }}
    >
      <span style={{ color: brand.gold }}>DEMO DATA</span> — the dashboard is pointed at the demo
      backend. Changes here do not affect production.
    </div>
  )
}
